/**
 * Maps the short product-type code embedded in an asset-tag barcode (the
 * middle segment of `[country]-[productType]-[serial]`) to the catalog
 * product it represents.
 *
 * Codes are matched case-insensitively and ignore whitespace / dashes /
 * underscores. The catalog match against `InventoryProduct.name` uses the
 * same normalization, so e.g. "Housekeeping Cart" matches both "House
 * Keeping Cart" and "housekeeping-cart".
 */

export type ProductTypeMapping = {
  /** Asset-tag code as it appears on the printed barcode (e.g. "Sbase"). */
  code: string;
  /** Catalog product name to look up in the InventoryProduct list. */
  productName: string;
  /** Human-readable label shown in the UI (matches the printed asset-tag intent). */
  label: string;
};

export const PRODUCT_TYPE_MAPPINGS: ProductTypeMapping[] = [
  { code: 'Sbase', productName: 'Base - Static',     label: 'Static Base - Metal' },
  { code: 'Rbase', productName: 'Universal Base',    label: 'Universal Base' },
  { code: 'TIME',  productName: 'TIM-E Bot',         label: 'TIM-E Bot' },
  { code: 'Plat',  productName: 'Universal Platform', label: 'Universal Platform' },
  { code: 'Lid',   productName: 'Universal Lids',    label: 'Universal Lids' },
  { code: 'Cons',  productName: 'Concession Cart',   label: 'Concession Cart' },
  { code: 'Garg',  productName: 'Garage',            label: 'Garage' },
  { code: 'Bin',   productName: 'Universal Bins',    label: 'Universal Bins' },
  { code: 'HK',    productName: 'Housekeeping Cart', label: 'House Keeping Cart' },
  { code: 'BIME',  productName: 'BIM-E bot',         label: 'BIM-E Bot' },
];

/**
 * Fallback rules for tag codes whose mapped product name no longer exists in
 * the catalog — usually because the product was voided and replaced by one or
 * more differently named ones (e.g. "Bin" → "Wheeled Bins", "Aluminum Bin With
 * Door"). Instead of resolving to nothing, the code recommends every active
 * product whose name contains the keyword.
 */
export type ProductKeywordRule = {
  /** Tag code the rule fires on, matched as a normalized prefix (BIN also covers BINS). */
  code: string;
  /** Word that must appear in the catalog product name. */
  keyword: string;
  /** Names containing any of these rank above the other keyword matches. */
  prefer?: string[];
};

export const PRODUCT_KEYWORD_RULES: ProductKeywordRule[] = [
  { code: 'rbase', keyword: 'base', prefer: ['wheeled', 'rolling'] },
  { code: 'sbase', keyword: 'base', prefer: ['static'] },
  { code: 'bin', keyword: 'bin' },
  { code: 'lid', keyword: 'lid' },
];

function normalize(s: string): string {
  return s.toLowerCase().replace(/[\s\-_]+/g, '');
}

export function findProductTypeMapping(code: string): ProductTypeMapping | undefined {
  if (!code) return undefined;
  const k = normalize(code);
  return PRODUCT_TYPE_MAPPINGS.find((m) => normalize(m.code) === k);
}

export function findProductKeywordRule(code: string): ProductKeywordRule | undefined {
  if (!code) return undefined;
  const k = normalize(code);
  /** Longest code first so RBASE/SBASE win over any shorter rule that also prefixes them. */
  return [...PRODUCT_KEYWORD_RULES]
    .sort((a, b) => b.code.length - a.code.length)
    .find((r) => k.startsWith(normalize(r.code)));
}

/**
 * Products a scanned tag code could refer to, best guess first: the exact
 * mapping when its product is still active, then keyword matches. Returns an
 * empty list when nothing fits, so callers can leave the product unset rather
 * than guess.
 */
export function suggestProductsByTypeCode<T extends { id: string; name: string; catalogStatus?: string }>(
  code: string,
  products: T[]
): T[] {
  if (!code) return [];
  const active = products.filter((p) => p.catalogStatus !== 'Void');
  const suggestions: T[] = [];
  const add = (p: T | undefined) => {
    if (p && !suggestions.some((existing) => existing.id === p.id)) suggestions.push(p);
  };

  add(resolveProductByTypeCode(code, active));

  const rule = findProductKeywordRule(code);
  if (rule) {
    const keyword = normalize(rule.keyword);
    const prefer = (rule.prefer ?? []).map(normalize);
    const matches = active.filter((p) => normalize(p.name).includes(keyword));
    matches
      .sort((a, b) => {
        const rank = (p: T) => {
          const n = normalize(p.name);
          if (prefer.some((w) => n.includes(w))) return 0;
          if (n.startsWith(keyword)) return 1;
          return 2;
        };
        return rank(a) - rank(b) || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      })
      .forEach(add);
  }

  return suggestions;
}

/**
 * Resolve the catalog product that a barcode's product-type code refers to.
 * Returns undefined if the code isn't in our mapping table or if no product
 * with the matching name exists in the catalog (e.g. user hasn't created it
 * yet).
 */
export function resolveProductByTypeCode<T extends { id: string; name: string; catalogStatus?: string }>(
  code: string,
  products: T[]
): T | undefined {
  const mapping = findProductTypeMapping(code);
  if (!mapping) return undefined;
  const target = normalize(mapping.productName);
  return products.find((p) => normalize(p.name) === target && p.catalogStatus !== 'Void');
}
